import { FONT, FONT_SIZE, LINE_HEIGHT, FONT_WEIGHT_NORMAL, FONT_WEIGHT_BOLD, COLORS, GRID_SIZE, TEXT_PADDING_X_UNITS, TEXT_PADDING_TOP_PX, TEXT_PADDING_BOTTOM_PX, TEXT_BOTTOM_TRIM_PX, SELECT_FILL_ALPHA, SELECT_FILL_COLOR, EDITING_FILL_COLOR, SELECT_CORNER_RADIUS_PX, SELECT_INSET_STROKE, EDITING_SHADOW_COLOR, EDITING_SHADOW_BLUR_PX, EDITING_SHADOW_OFFSET_Y_PX, LABEL_FONT_FAMILY, LABEL_FONT_SIZE, HANDLE_HALO_ARM_PX, HANDLE_HALO_PADDING_PX, PAPER_A8_STROKE_CLAMP, PAPER_A4_STROKE_CLAMP, PAPER_HIDE_BELOW_SCALE, PAPER_LABEL_HIDE_BELOW_SCALE, TEXT_BASELINE_SHIFT_PX, AREA_SELECT_OUTLINE_PX, AREA_SELECT_RECT_STROKE_PX, AREA_SELECT_RECT_ALPHA, CODE_HIGHLIGHT_INSET_TOP_PX, CODE_HIGHLIGHT_INSET_BOTTOM_PX, SEARCH_MARK_STROKE_PX } from './constants.js'
import { toPixels } from './grid.js'
import type { Block, TextMetrics, Camera, ConnectionSelection, PaperSpec } from './types.js'
import { getDefaultPaperType } from './paper.js'
import { parseMarkdownCodeBlock } from './lib/markdownCode.js'
import { rangeToRects, type SearchLayout, type SearchMark, type SearchRange, type SearchSpanStyle } from './lib/searchEngine.js'

type Point = { x: number, y: number }

type InlineToken = {
  text: string
  bold?: boolean
  italic?: boolean
  href?: string
  code?: boolean
}

type LayoutSpan = {
  text: string
  width: number
  bold?: boolean
  italic?: boolean
  href?: string
  code?: boolean
}

type LayoutLine = {
  spans: LayoutSpan[]
  width: number
  separatorBefore: string
}

type LinkRect = {
  href: string
  x: number
  y: number
  width: number
  height: number
}

type LayoutResult = {
  lines: LayoutLine[]
  width: number
  height: number
  linkRects: LinkRect[]
}

type RenderSegmentLayout = {
  kind: 'markdown' | 'code'
  layout: LayoutResult
  yOffset: number
}

type ParsedLayoutResult = {
  segments: RenderSegmentLayout[]
  width: number
  height: number
  linkRects: LinkRect[]
}

type ConnectionContext = {
  outgoing: Set<string>
  incoming: Set<string>
  connected: Set<string>
  draftSourceId?: string
  draftTargetId?: string
}

type ConnectionGeometry = {
  startAnchor: Point
  startRail: Point
  endAnchor: Point
  endRail: Point
  startShift: number
  endShift: number
}

type CanvasPalette = {
  paper: string
  surface: string
  text: string
  handle: string
  baseGrid: string
  paperGrid: string
  action: string
  selectionFill: string
  selectionStroke: string
  codeText: string
  label: string
  labelShadow: string
}

const DEFAULT_CANVAS_PALETTE: CanvasPalette = {
  paper: COLORS.BACKGROUND,
  surface: EDITING_FILL_COLOR,
  text: COLORS.TEXT,
  handle: COLORS.HANDLES,
  baseGrid: COLORS.BASE_GRID,
  paperGrid: COLORS.PAPER_GRID,
  action: COLORS.CMD_BUTTON,
  selectionFill: SELECT_FILL_COLOR,
  selectionStroke: SELECT_INSET_STROKE,
  codeText: COLORS.BACKGROUND,
  label: COLORS.BASE_GRID,
  labelShadow: 'rgba(0, 0, 0, 0.12)',
}

export class Canvas {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private layoutCache = new Map<string, LayoutResult>()
  private devicePixelRatio: number
  private palette: CanvasPalette = DEFAULT_CANVAS_PALETTE
  private camera: Camera = { scale: 1, tx: 0, ty: 0 }
  private mobileEditing = false
  private paperSpec: PaperSpec = getDefaultPaperType()
  private resizePreview?: { id: string, x: number, y: number, width: number, height: number }
  // Fluid multi-resize: followers preview the same absolute width at their own
  // origin and current height; only the grabbed block's preview tracks pins.
  private resizeFollowerPreviews?: Array<{ x: number, y: number, width: number, height: number }>
  private connectionDraft?: ConnectionGeometry
  private areaSelectionRect?: { x: number, y: number, width: number, height: number }
  private areaSelectAnchorId?: string
  private connectionContext: ConnectionContext = {
    outgoing: new Set(),
    incoming: new Set(),
    connected: new Set(),
  }
  private selectedConnections: ConnectionSelection[] = []
  private searchMarksByBlock = new Map<string, Array<{ range: SearchRange, active: boolean }>>()

  private drawEditingPlaceholder(block: Block): void {
    const x = toPixels(block.xyzw[0])
    const y = toPixels(block.xyzw[1])
    const width = toPixels(block.xyzw[3])
    const metrics = this.measureText(block.text, width)
    const height = metrics.height
    this.ctx.save()
    const scale = Math.max(0.0001, this.camera.scale || 1)
    this.ctx.shadowColor = EDITING_SHADOW_COLOR
    this.ctx.shadowBlur = EDITING_SHADOW_BLUR_PX / scale
    this.ctx.shadowOffsetX = 0
    this.ctx.shadowOffsetY = EDITING_SHADOW_OFFSET_Y_PX / scale
    this.ctx.fillStyle = this.palette.surface
    this.ctx.globalAlpha = SELECT_FILL_ALPHA
    this.fillRoundedRect(x, y, width, height, SELECT_CORNER_RADIUS_PX)
    this.ctx.restore()
  }
  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
    this.devicePixelRatio = window.devicePixelRatio || 1
    this.refreshPalette()
    
    this.setupCanvas()
    this.setupTextRendering()
  }

  private setupCanvas(): void {
    // Handle high-DPI displays
    const rect = this.canvas.getBoundingClientRect()
    this.canvas.width = rect.width * this.devicePixelRatio
    this.canvas.height = rect.height * this.devicePixelRatio
    // Do not pre-scale here; combine DPR with camera per frame
    
    // Set canvas CSS size
    this.canvas.style.width = rect.width + 'px'
    this.canvas.style.height = rect.height + 'px'
  }

  private setupTextRendering(): void {
    this.ctx.font = `${FONT_SIZE}px ${FONT}`
    this.ctx.textBaseline = 'top'
    this.ctx.fillStyle = this.palette.text
  }

  private cssToken(style: CSSStyleDeclaration, name: string, fallback: string): string {
    return style.getPropertyValue(name).trim() || fallback
  }

  private refreshPalette(): void {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
      this.palette = DEFAULT_CANVAS_PALETTE
      return
    }
    const style = window.getComputedStyle(this.canvas)
    const paper = this.cssToken(style, '--paper', COLORS.BACKGROUND)
    const surface = this.cssToken(style, '--surface', EDITING_FILL_COLOR)
    const text = this.cssToken(style, '--text-primary', COLORS.TEXT)
    const baseGrid = this.cssToken(style, '--base-grid', COLORS.BASE_GRID)
    this.palette = {
      paper,
      surface,
      text,
      handle: text,
      baseGrid,
      paperGrid: this.cssToken(style, '--paper-grid', COLORS.PAPER_GRID),
      action: this.cssToken(style, '--cmd-red', COLORS.CMD_BUTTON),
      selectionFill: this.cssToken(style, '--bg-block', SELECT_FILL_COLOR),
      selectionStroke: this.cssToken(style, '--border-divider', SELECT_INSET_STROKE),
      codeText: paper,
      label: this.cssToken(style, '--canvas-watermark-text', baseGrid),
      labelShadow: this.cssToken(style, '--canvas-watermark-shadow', 'rgba(0, 0, 0, 0.12)'),
    }
  }

  clear(): void {
    // Reset transform and clear full pixel buffer
    this.ctx.setTransform(1, 0, 0, 1, 0, 0)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  drawGrid(): void {
    this.drawPaperGuides()
  }

  drawBlocks(blocks: Block[], selectedBlockIds: string[] = [], editingBlockId?: string): void {
    const selectedSet = new Set(selectedBlockIds)
    blocks.forEach(block => {
      if (editingBlockId && editingBlockId === block.ided[0]) return
      const isEditing = editingBlockId === block.ided[0]
      this.drawBlock(block, isEditing)
      this.drawMarkers(block, selectedSet)
    })
    
    if (!selectedSet.size && !editingBlockId) return
    blocks.forEach(block => {
      const isEditing = editingBlockId === block.ided[0]
      if (!selectedSet.has(block.ided[0]) && !isEditing) return
      if (isEditing) return
      this.drawSelectionFrame(block, isEditing)
    })
  }

  private drawBlock(block: Block, isEditing: boolean = false): void {
    const x = toPixels(block.xyzw[0])
    const y = toPixels(block.xyzw[1])
    const width = toPixels(block.xyzw[3])
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const innerX = x + padX
    // First line uses the first grid row; only a small optical top nudge
    const innerY = y + TEXT_PADDING_TOP_PX + TEXT_BASELINE_SHIFT_PX
    const innerW = Math.max(0, width - 2 * padX)
    
    // Skip canvas text rendering when ContentEditable is active
    if (isEditing) return
    
    const parsedLayout = this.getParsedLayout(block.text, innerW)
    
    // Draw text with inline styles and link underlines
    this.ctx.fillStyle = this.palette.text
    parsedLayout.segments.forEach(segment => {
      segment.layout.lines.forEach((line, lineIndex) => {
        let cursorX = innerX
        const lineY = innerY + segment.yOffset + (lineIndex * LINE_HEIGHT)
        if (segment.kind === 'code' && line.width > 0) {
          this.drawCodeHighlightBand(cursorX, lineY, line.width)
        }
        line.spans.forEach(span => {
          if (segment.kind === 'markdown' && span.code && span.width > 0) {
            this.drawCodeHighlightBand(cursorX, lineY, span.width)
          }
          this.applyFontStyle(span)
          this.ctx.fillStyle = (segment.kind === 'code' || (segment.kind === 'markdown' && span.code)) ? this.palette.codeText : this.palette.text
          this.ctx.fillText(span.text, cursorX, lineY)
          if (span.href || block.meta?.index === 1) {
            const underlineOffsetPx = 4
            const underlineY = lineY + FONT_SIZE + underlineOffsetPx
            this.ctx.beginPath()
            this.ctx.moveTo(cursorX, underlineY)
            this.ctx.lineTo(cursorX + span.width, underlineY)
            this.ctx.lineWidth = 2 / this.devicePixelRatio
            this.ctx.strokeStyle = this.palette.text
            this.ctx.stroke()
          }
          cursorX += span.width
        })
      })
    })

    this.drawSearchMarks(block, parsedLayout, innerX, innerY)

    // No extra dotted tail; connection stub is drawn alongside markers instead.
  }

  private drawMarkers(block: Block, selectedSet: Set<string>): void {
    const blockId = block.ided[0]
    const isSelected = selectedSet.has(blockId)
    const isConnected = this.connectionContext.connected.has(blockId)
    if (!isConnected && !isSelected) return

    const startBase = this.getStartHandleBase(block)
    const endBase = this.getEndHandleBase(block)
    const hasIncoming = this.hasIncoming(blockId)
    const hasOutgoing = this.hasOutgoing(blockId)
    const showStart = hasIncoming
    const showEnd = hasOutgoing || isSelected
    if (!showStart && !showEnd) return

    const startOffset = showStart && hasIncoming ? GRID_SIZE : 0
    const endOffset = showEnd && hasOutgoing ? GRID_SIZE : 0

    const preview = this.resizePreview && this.resizePreview.id === block.ided[0] ? this.resizePreview : undefined
    let startX = (preview ? preview.x : startBase.x) + startOffset
    let startY = preview ? preview.y : startBase.y
    let endX = (preview ? preview.x + preview.width : endBase.x) - endOffset
    let endY = preview ? preview.y + preview.height : endBase.y
    const startPos = { x: startX, y: startY }
    const endPos = { x: endX, y: endY }

    // Draw crosshair + markers at text block external boundaries
    this.ctx.strokeStyle = this.palette.handle
    this.ctx.lineWidth = 2 / this.devicePixelRatio

    // Stub: connect last line end to End+ for blocks with outgoing links.
    if (hasOutgoing) {
      this.drawOutgoingStub(block, endPos, preview)
    }

    if (showStart) {
      this.drawHandleHalo(startPos.x, startPos.y)
      this.drawHandleDot(startPos.x, startPos.y)
    }
    if (showEnd) {
      const addShadow = isSelected && !hasOutgoing
      this.drawHandleHalo(endPos.x, endPos.y, addShadow)
      this.drawHandleDot(endPos.x, endPos.y)
    }
  }

  private drawHandleHalo(centerX: number, centerY: number, withShadow: boolean = false): void {
    // Carve a small gap around the handle to visually detach lines/stubs.
    const radius = HANDLE_HALO_ARM_PX + HANDLE_HALO_PADDING_PX
    this.ctx.save()
    if (withShadow) {
      const scale = Math.max(0.0001, this.camera.scale || 1)
      this.ctx.shadowColor = 'rgba(0, 0, 0, 0.16)'
      this.ctx.shadowBlur = 12 / scale
      this.ctx.shadowOffsetX = 0
      this.ctx.shadowOffsetY = 6 / scale
    }
    this.ctx.fillStyle = this.palette.paper
    this.ctx.beginPath()
    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
    this.ctx.fill()
    this.ctx.restore()
  }

  private highlightBandRect(x: number, lineY: number, width: number): { x: number, y: number, width: number, height: number } {
    const insetTop = CODE_HIGHLIGHT_INSET_TOP_PX
    const insetBottom = CODE_HIGHLIGHT_INSET_BOTTOM_PX
    const bandBaselineShift = -TEXT_BASELINE_SHIFT_PX
    return {
      x,
      y: lineY + insetTop + bandBaselineShift,
      width,
      height: LINE_HEIGHT - insetTop - insetBottom,
    }
  }

  private drawCodeHighlightBand(x: number, lineY: number, width: number): void {
    const band = this.highlightBandRect(x, lineY, width)
    this.ctx.save()
    this.ctx.fillStyle = this.palette.text
    this.ctx.globalAlpha = 1
    this.fillRoundedRect(band.x, band.y, band.width, band.height, 0)
    this.ctx.restore()
  }

  setSearchMarks(marks: SearchMark[]): void {
    this.searchMarksByBlock = new Map()
    marks.forEach(mark => {
      const existing = this.searchMarksByBlock.get(mark.blockId)
      const entry = { range: mark.range, active: mark.active }
      if (existing) {
        existing.push(entry)
        return
      }
      this.searchMarksByBlock.set(mark.blockId, [entry])
    })
  }

  getSearchLayout(text: string, blockWidthPx: number): SearchLayout {
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const innerWidth = Math.max(0, blockWidthPx - 2 * padX)
    return this.flattenParsedLayout(this.getParsedLayout(text, innerWidth))
  }

  private flattenParsedLayout(parsed: ParsedLayoutResult): SearchLayout {
    const lines: SearchLayout['lines'] = []
    parsed.segments.forEach(segment => {
      segment.layout.lines.forEach((line, lineIndex) => {
        lines.push({
          spans: line.spans.map(span => ({
            text: span.text,
            width: span.width,
            bold: span.bold,
            italic: span.italic,
            code: span.code || segment.kind === 'code' || undefined,
          })),
          yPx: segment.yOffset + lineIndex * LINE_HEIGHT,
          separatorBefore: lineIndex === 0 && lines.length > 0 ? ' ' : line.separatorBefore,
        })
      })
    })
    return { lines }
  }

  private measureSearchSpan = (text: string, style: SearchSpanStyle): number => {
    this.applyFontStyle(style)
    return this.ctx.measureText(text).width
  }

  private drawSearchMarks(block: Block, parsedLayout: ParsedLayoutResult, innerX: number, innerY: number): void {
    const marks = this.searchMarksByBlock.get(block.ided[0])
    if (!marks || !marks.length) return
    const layout = this.flattenParsedLayout(parsedLayout)
    marks.forEach(mark => {
      rangeToRects(layout, mark.range, this.measureSearchSpan).forEach(rect => {
        const band = this.highlightBandRect(innerX + rect.x, innerY + rect.y, rect.width)
        if (mark.active) {
          this.drawActiveSearchBand(band, layout, rect.y, innerX, innerY)
        } else {
          this.drawSearchMarkOutline(band)
        }
      })
    })
  }

  private drawSearchMarkOutline(band: { x: number, y: number, width: number, height: number }): void {
    const scale = this.camera.scale > 0 ? this.camera.scale : 1
    this.ctx.save()
    this.ctx.strokeStyle = this.palette.action
    this.ctx.lineWidth = SEARCH_MARK_STROKE_PX / scale
    this.ctx.strokeRect(band.x, band.y, band.width, band.height)
    this.ctx.restore()
  }

  private drawActiveSearchBand(
    band: { x: number, y: number, width: number, height: number },
    layout: SearchLayout,
    lineYPx: number,
    innerX: number,
    innerY: number
  ): void {
    this.ctx.save()
    this.ctx.fillStyle = this.palette.action
    this.ctx.fillRect(band.x, band.y, band.width, band.height)
    // Invert the covered slice: clip to the band, repaint the line in paper.
    this.ctx.beginPath()
    this.ctx.rect(band.x, band.y, band.width, band.height)
    this.ctx.clip()
    const line = layout.lines.find(candidate => candidate.yPx === lineYPx)
    if (line) {
      let cursorX = innerX
      const lineY = innerY + lineYPx
      line.spans.forEach(span => {
        this.applyFontStyle(span)
        this.ctx.fillStyle = this.palette.paper
        this.ctx.fillText(span.text, cursorX, lineY)
        cursorX += span.width
      })
    }
    this.ctx.restore()
  }

  private drawHandleDot(centerX: number, centerY: number): void {
    const radius = 4
    this.ctx.fillStyle = this.palette.handle
    this.ctx.beginPath()
    this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
    this.ctx.fill()
  }

  private drawOutgoingStub(block: Block, endPos: Point, preview?: { id: string, x: number, y: number, width: number, height: number }): void {
    const blockX = preview ? preview.x : toPixels(block.xyzw[0])
    const blockWidth = preview ? preview.width : toPixels(block.xyzw[3])
    const parsed = parseMarkdownCodeBlock(block.text)
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const innerX = blockX + padX
    const innerW = Math.max(0, blockWidth - 2 * padX)
    const layout = this.getLayout(parsed.content, innerW, { literal: parsed.isCodeBlock })
    const lastLine = layout.lines[layout.lines.length - 1]
    const lastLineWidth = lastLine ? lastLine.width : 0
    const charPad = this.ctx.measureText(' ').width
    const rawStartX = innerX + lastLineWidth + charPad
    const minStub = GRID_SIZE
    let startX = rawStartX
    if (endPos.x - startX < minStub) {
      startX = Math.max(innerX, endPos.x - minStub)
    }
    const y = endPos.y
    this.ctx.save()
    this.ctx.strokeStyle = this.palette.text
    this.ctx.lineWidth = 2 / this.devicePixelRatio
    this.ctx.lineCap = 'round'
    this.ctx.beginPath()
    this.ctx.moveTo(startX, y)
    this.ctx.lineTo(endPos.x, y)
    this.ctx.stroke()
    this.ctx.restore()
  }

  private drawSelectionFrame(block: Block, isEditing: boolean = false): void {
    const x = toPixels(block.xyzw[0])
    const y = toPixels(block.xyzw[1])
    const width = toPixels(block.xyzw[3])
    const preview = this.resizePreview && this.resizePreview.id === block.ided[0] ? this.resizePreview : undefined
    // Use preview height when resizing; otherwise measure once.
    let height: number
    if (preview) {
      height = preview.height
    } else {
      if (isEditing && (!block.text || block.text.length === 0)) {
        height = LINE_HEIGHT
      } else {
        const metrics = this.measureText(block.text, width)
        height = metrics.height
      }
    }
    // Subtle background highlight behind the selected block
    this.ctx.save()
    const prevComp = this.ctx.globalCompositeOperation
    this.ctx.globalCompositeOperation = 'destination-over'
    this.ctx.globalAlpha = SELECT_FILL_ALPHA
    this.ctx.fillStyle = this.palette.selectionFill
    if (preview) {
      this.ctx.fillRect(x, y, width, height)
    } else {
      this.fillRoundedRect(x, y, width, height, SELECT_CORNER_RADIUS_PX)
    }
    this.ctx.globalCompositeOperation = prevComp
    this.ctx.restore()

    if (!preview) {
      this.ctx.save()
      this.ctx.globalCompositeOperation = 'destination-over'
      this.ctx.strokeStyle = this.palette.selectionStroke
      this.ctx.lineWidth = 1 / this.devicePixelRatio
      this.strokeRoundedRect(x, y, width, height, SELECT_CORNER_RADIUS_PX)
      this.ctx.restore()
    }
  }

  private fillRoundedRect(x: number, y: number, width: number, height: number, radius: number): void {
    const r = Math.min(radius, width / 2, height / 2)
    this.ctx.beginPath()
    this.ctx.moveTo(x + r, y)
    this.ctx.lineTo(x + width - r, y)
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + r)
    this.ctx.lineTo(x + width, y + height - r)
    this.ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
    this.ctx.lineTo(x + r, y + height)
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - r)
    this.ctx.lineTo(x, y + r)
    this.ctx.quadraticCurveTo(x, y, x + r, y)
    this.ctx.closePath()
    this.ctx.fill()
  }

  private strokeRoundedRect(x: number, y: number, width: number, height: number, radius: number): void {
    const r = Math.min(radius, width / 2, height / 2)
    this.ctx.beginPath()
    this.ctx.moveTo(x + r, y)
    this.ctx.lineTo(x + width - r, y)
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + r)
    this.ctx.lineTo(x + width, y + height - r)
    this.ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
    this.ctx.lineTo(x + r, y + height)
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - r)
    this.ctx.lineTo(x, y + r)
    this.ctx.quadraticCurveTo(x, y, x + r, y)
    this.ctx.closePath()
    this.ctx.stroke()
  }

  measureText(text: string, blockWidth: number): TextMetrics {
    // Wrap using inner content width (subtract horizontal padding)
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const innerWidth = Math.max(0, blockWidth - 2 * padX)
    const layout = this.getParsedLayout(text, innerWidth)
    return { width: layout.width, height: layout.height }
  }

  setCamera(camera: Camera): void {
    this.camera = camera
  }

  setPaperSpec(spec: PaperSpec): void {
    this.paperSpec = spec
  }

  private applyCameraTransform(): void {
    const dpr = this.devicePixelRatio
    const s = this.camera.scale
    const tx = this.camera.tx
    const ty = this.camera.ty
    // Set combined transform: DPR first, then camera
    this.ctx.setTransform(dpr * s, 0, 0, dpr * s, dpr * tx, dpr * ty)
  }

  private drawPaperGuides(): void {
    const dpr = this.devicePixelRatio
    const scale = this.camera.scale
    const pitchX = this.paperSpec.nominal.w * GRID_SIZE
    const pitchY = this.paperSpec.nominal.h * GRID_SIZE
    const columnPitchX = this.paperSpec.quarter.w * GRID_SIZE
    const columnPitchY = this.paperSpec.quarter.h * GRID_SIZE
    const viewWidth = this.canvas.width / (dpr * scale)
    const viewHeight = this.canvas.height / (dpr * scale)
    const originX = -this.camera.tx / scale
    const originY = -this.camera.ty / scale
    const startX = Math.floor(originX / pitchX) * pitchX - pitchX
    const startY = Math.floor(originY / pitchY) * pitchY - pitchY
    const endX = originX + viewWidth + pitchX
    const endY = originY + viewHeight + pitchY
    const desiredScreenStrokeA4 = Math.max(PAPER_A4_STROKE_CLAMP.min, Math.min(PAPER_A4_STROKE_CLAMP.max, PAPER_A4_STROKE_CLAMP.mid * scale))
    const strokeWidthA4 = desiredScreenStrokeA4 / (dpr * scale)
    const desiredScreenStrokeA8 = Math.max(PAPER_A8_STROKE_CLAMP.min, Math.min(PAPER_A8_STROKE_CLAMP.max, PAPER_A8_STROKE_CLAMP.mid * scale))
    const strokeWidthA8 = desiredScreenStrokeA8 / (dpr * scale)

    this.ctx.save()
    this.ctx.strokeStyle = this.palette.baseGrid

    // Draw column lines; skip overlaps with nominal (every 4th line)
    const hideA8 = scale < PAPER_HIDE_BELOW_SCALE
    if (!hideA8) {
      this.ctx.lineWidth = strokeWidthA8
      const colStartX = Math.floor(originX / columnPitchX) * columnPitchX - columnPitchX
      const colStartY = Math.floor(originY / columnPitchY) * columnPitchY - columnPitchY
      const colEndX = originX + viewWidth + columnPitchX
      const colEndY = originY + viewHeight + columnPitchY
      for (let i = 0, x = colStartX; x <= colEndX; i++, x += columnPitchX) {
        this.ctx.beginPath()
        this.ctx.moveTo(x, startY)
        this.ctx.lineTo(x, endY)
        this.ctx.stroke()
      }
      for (let j = 0, y = colStartY; y <= colEndY; j++, y += columnPitchY) {
        this.ctx.beginPath()
        this.ctx.moveTo(startX, y)
        this.ctx.lineTo(endX, y)
        this.ctx.stroke()
      }
    }

    // Draw nominal paper guides on top
    this.ctx.strokeStyle = this.palette.paperGrid
    this.ctx.lineWidth = strokeWidthA4
    for (let x = startX; x <= endX; x += pitchX) {
      this.ctx.beginPath()
      this.ctx.moveTo(x, startY)
      this.ctx.lineTo(x, endY)
      this.ctx.stroke()
    }
    for (let y = startY; y <= endY; y += pitchY) {
      this.ctx.beginPath()
      this.ctx.moveTo(startX, y)
      this.ctx.lineTo(endX, y)
      this.ctx.stroke()
    }

    this.ctx.restore()
  }

  render(blocks: Block[], snappedBlocks: Block[], selectedBlockIds: string[] = [], editingBlockId?: string): void {
    this.refreshPalette()
    this.clear()
    this.applyCameraTransform()
    this.drawGrid()
    // Ensure text rendering state is correct after transform
    this.setupTextRendering()
    // Draw paper labels behind blocks for subtlety
    this.drawPaperLabels()
    this.drawConnections(snappedBlocks, blocks)
    this.drawConnectionDraft()
    this.drawResizePreview()
    this.drawBlocks(blocks, selectedBlockIds, editingBlockId)
    if (this.areaSelectAnchorId) {
      const anchor = blocks.find(block => block.ided[0] === this.areaSelectAnchorId)
      if (anchor) {
        this.drawAreaSelectOutline(anchor)
      }
    }
    if (this.areaSelectionRect) {
      this.drawAreaSelectionRect(this.areaSelectionRect)
    }
    if (editingBlockId && this.mobileEditing) {
      const editing = blocks.find(b => b.ided[0] === editingBlockId)
      if (editing) this.drawEditingPlaceholder(editing)
    }
  }

  setResizePreview(preview?: { id: string, x: number, y: number, width: number, height: number }, followers?: Array<{ x: number, y: number, width: number, height: number }>): void {
    this.resizePreview = preview
    this.resizeFollowerPreviews = preview ? followers : undefined
  }

  setConnectionDraft(draft?: ConnectionGeometry): void {
    this.connectionDraft = draft
  }

  setAreaSelectionRect(rect?: { x: number, y: number, width: number, height: number }): void {
    this.areaSelectionRect = rect
  }

  setAreaSelectAnchor(id?: string): void {
    this.areaSelectAnchorId = id
  }

  setConnectionContext(context: ConnectionContext): void {
    this.connectionContext = context
  }

  setSelectedConnections(selection: ConnectionSelection[]): void {
    this.selectedConnections = selection
  }

  setMobileEditing(isMobile: boolean): void {
    this.mobileEditing = isMobile
  }

  private drawConnections(snapped: Block[], actual: Block[]): void {
    if (!snapped.length) return
    const snappedMap = new Map(snapped.map(b => [b.ided[0], b]))
    const actualMap = new Map(actual.map(b => [b.ided[0], b]))
    const baseStroke = 2 / this.devicePixelRatio
    const selectedColor = this.palette.action
    const highlightSet = new Set(
      this.selectedConnections.map(sel => `${sel.sourceId}→${sel.targetId}`)
    )
    this.ctx.save()
    this.ctx.lineWidth = baseStroke
    this.ctx.lineCap = 'round'
    snapped.forEach(source => {
      if (!source.link.length) return
      source.link.forEach(targetId => {
        const target = snappedMap.get(targetId)
        if (!target) return
        const geometry = this.getConnectionGeometryForBlocks(
          source,
          target,
          undefined,
          {
            source: actualMap.get(source.ided[0]),
            target: actualMap.get(targetId)
          }
        )
        const path = this.buildConnectionPath(geometry)
        const isHighlighted = highlightSet.has(`${source.ided[0]}→${targetId}`)
        const strokeColor = isHighlighted ? selectedColor : this.palette.text
        this.strokeConnectionPath(path, strokeColor, baseStroke)
      })
    })
    this.ctx.restore()
  }

  private drawConnectionDraft(): void {
    if (!this.connectionDraft) return
    const strokeWidth = 2 / this.devicePixelRatio
    this.ctx.save()
    this.ctx.lineWidth = strokeWidth
    this.ctx.lineCap = 'round'
    const path = this.buildConnectionPath(this.connectionDraft)
    this.strokeConnectionPath(path, this.palette.action, strokeWidth)
    this.ctx.restore()
  }

  private drawResizePreview(): void {
    if (!this.resizePreview) return
    this.ctx.save()
    const prevComp = this.ctx.globalCompositeOperation
    this.ctx.globalCompositeOperation = 'destination-over'
    this.ctx.globalAlpha = SELECT_FILL_ALPHA
    this.ctx.fillStyle = this.palette.paperGrid
    const { x, y, width, height } = this.resizePreview
    this.ctx.fillRect(x, y, width, height)
    this.resizeFollowerPreviews?.forEach(follower => {
      this.ctx.fillRect(follower.x, follower.y, follower.width, follower.height)
    })
    this.ctx.globalCompositeOperation = prevComp
    this.ctx.restore()
  }

  private drawAreaSelectOutline(block: Block): void {
    const x = toPixels(block.xyzw[0])
    const y = toPixels(block.xyzw[1])
    const width = toPixels(block.xyzw[3])
    const metrics = this.measureText(block.text, width)
    const height = metrics.height
    this.ctx.save()
    this.ctx.strokeStyle = this.palette.baseGrid
    this.ctx.lineWidth = AREA_SELECT_OUTLINE_PX / this.devicePixelRatio
    this.strokeRoundedRect(x, y, width, height, SELECT_CORNER_RADIUS_PX)
    this.ctx.restore()
  }

  private drawAreaSelectionRect(rect: { x: number, y: number, width: number, height: number }): void {
    const { x, y, width, height } = rect
    if (width <= 0 || height <= 0) return
    this.ctx.save()
    this.ctx.strokeStyle = this.palette.baseGrid
    this.ctx.globalAlpha = AREA_SELECT_RECT_ALPHA
    this.ctx.lineWidth = AREA_SELECT_RECT_STROKE_PX / this.devicePixelRatio
    this.strokeRoundedRect(x, y, width, height, SELECT_CORNER_RADIUS_PX)
    this.ctx.restore()
  }

  private buildConnectionPath(geometry: ConnectionGeometry): Point[] {
    const { startAnchor, startRail, endAnchor, endRail, startShift, endShift } = geometry
    const start: Point = { x: startAnchor.x - startShift, y: startAnchor.y }
    const end: Point = { x: endAnchor.x + endShift, y: endAnchor.y }
    const points: Point[] = []
    const push = (pt: Point) => {
      const last = points[points.length - 1]
      if (!last || Math.abs(last.x - pt.x) > 0.001 || Math.abs(last.y - pt.y) > 0.001) {
        points.push(pt)
      }
    }
    const needsHorizontal = Math.abs(endRail.x - startRail.x) > 0.001
    const gridPitch = GRID_SIZE * 2 // 32px rail cadence
    const clearance = GRID_SIZE // keep 16px between rail and handles for the elbows

    push(start)
    push(startRail)

    if (needsHorizontal) {
      const verticalGap = Math.abs(endRail.y - startRail.y)
      const tightGap = verticalGap <= gridPitch
      if (tightGap) {
        push({ x: startRail.x, y: endRail.y })
        push({ x: endRail.x, y: endRail.y })
      } else {
        const rawMid = startRail.y + (endRail.y - startRail.y) / 2
        const snappedMid = Math.round(rawMid / gridPitch) * gridPitch
        const minY = Math.min(startRail.y, endRail.y)
        const maxY = Math.max(startRail.y, endRail.y)
        let railY = snappedMid
        const minAllowed = minY + clearance
        const maxAllowed = maxY - clearance
        if (maxAllowed <= minAllowed) {
          railY = rawMid
        } else {
          railY = clamp(railY, minAllowed, maxAllowed)
        }
        push({ x: startRail.x, y: railY })
        push({ x: endRail.x, y: railY })
      }
    }

    push(endRail)
    push(end)
    return points
  }

  private strokeConnectionPath(points: Point[], color: string, width: number): void {
    if (points.length < 2) return
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = width
    const radius = GRID_SIZE
    let current = points[0]
    this.ctx.beginPath()
    this.ctx.moveTo(current.x, current.y)
    for (let i = 1; i < points.length; i++) {
      const target = points[i]
      const next = points[i + 1]
      const seg1 = { x: target.x - current.x, y: target.y - current.y }
      const len1 = Math.hypot(seg1.x, seg1.y)
      if (len1 < 0.001) {
        current = target
        continue
      }
      if (!next) {
        this.ctx.lineTo(target.x, target.y)
        current = target
        break
      }
      const seg2 = { x: next.x - target.x, y: next.y - target.y }
      const len2 = Math.hypot(seg2.x, seg2.y)
      const cross = seg1.x * seg2.y - seg1.y * seg2.x
      const isCorner = Math.abs(cross) > 0.001 && len2 >= 0.001
      if (!isCorner) {
        this.ctx.lineTo(target.x, target.y)
        current = target
        continue
      }
      const usable = radius
      const norm1 = { x: seg1.x / len1, y: seg1.y / len1 }
      const norm2 = { x: seg2.x / len2, y: seg2.y / len2 }
      const startX = target.x - norm1.x * usable
      const startY = target.y - norm1.y * usable
      const endX = target.x + norm2.x * usable
      const endY = target.y + norm2.y * usable
      this.ctx.lineTo(startX, startY)
      this.ctx.quadraticCurveTo(target.x, target.y, endX, endY)
      current = { x: endX, y: endY }
    }
    const last = points[points.length - 1]
    if (Math.abs(current.x - last.x) > 0.001 || Math.abs(current.y - last.y) > 0.001) {
      this.ctx.lineTo(last.x, last.y)
    }
    this.ctx.stroke()
  }

  private hasOutgoing(id: string): boolean {
    return this.connectionContext.outgoing.has(id) || this.connectionContext.draftSourceId === id
  }

  private hasIncoming(id: string): boolean {
    return this.connectionContext.incoming.has(id) || this.connectionContext.draftTargetId === id
  }

  private getConnectionGeometryForBlocks(
    source: Block,
    target: Block,
    preview?: { id: string, x: number, y: number, width: number, height: number },
    anchors?: { source?: Block, target?: Block }
  ): ConnectionGeometry {
    const sourcePreview = preview && preview.id === source.ided[0] ? preview : undefined
    const targetPreview = preview && preview.id === target.ided[0] ? preview : undefined
    const sourceActual = sourcePreview ? undefined : anchors?.source
    const targetActual = targetPreview ? undefined : anchors?.target

    const startAnchor = sourcePreview
      ? { x: sourcePreview.x + sourcePreview.width, y: sourcePreview.y + sourcePreview.height }
      : this.getEndHandleBase(sourceActual ?? source)
    const startRailBase = this.getEndHandleBase(source)
    const startRail = { x: startRailBase.x, y: startAnchor.y }

    const endAnchor = targetPreview
      ? { x: targetPreview.x, y: targetPreview.y }
      : this.getStartHandleBase(targetActual ?? target)
    const endRailBase = this.getStartHandleBase(target)
    const endRail = { x: endRailBase.x, y: endAnchor.y }

    const startShift = this.hasOutgoing(source.ided[0]) ? GRID_SIZE : 0
    const endShift = this.hasIncoming(target.ided[0]) ? GRID_SIZE : 0
    return { startAnchor, startRail, endAnchor, endRail, startShift, endShift }
  }

  computeDraftGeometry(source: Block, target: Block, preview?: { id: string, x: number, y: number, width: number, height: number }): ConnectionGeometry {
    return this.getConnectionGeometryForBlocks(source, target, preview)
  }

  getConnectionPath(source: Block, target: Block, preview?: { id: string, x: number, y: number, width: number, height: number }): Point[] {
    const geometry = this.getConnectionGeometryForBlocks(source, target, preview)
    return this.buildConnectionPath(geometry)
  }

  private getStartHandleBase(block: Block): Point {
    return {
      x: toPixels(block.xyzw[0]),
      y: toPixels(block.xyzw[1]),
    }
  }

  private getEndHandleBase(block: Block): Point {
    const baseX = toPixels(block.xyzw[0])
    const baseY = toPixels(block.xyzw[1])
    const widthPx = toPixels(block.xyzw[3])
    const metrics = this.measureText(block.text, widthPx)
    return {
      x: baseX + widthPx,
      y: baseY + metrics.height,
    }
  }

  // Paper size labels along axes near the origin
  private drawPaperLabels(): void {
    if (this.camera.scale < PAPER_LABEL_HIDE_BELOW_SCALE) return
    const ctx = this.ctx
    ctx.save()
    ctx.globalAlpha = 1
    ctx.fillStyle = this.palette.label
    ctx.shadowColor = this.palette.labelShadow
    ctx.shadowBlur = 32
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 8
    const prevBaseline = ctx.textBaseline
    const prevAlign = ctx.textAlign as CanvasTextAlign
    const prevFont = ctx.font
    ctx.textAlign = 'right'
    ctx.font = `${LABEL_FONT_SIZE}px ${LABEL_FONT_FAMILY}`

    // Horizontal and vertical labels: slight offset from axes (0.5 grid = 8px)
    const gapWorld = GRID_SIZE / 2
    ctx.textBaseline = 'alphabetic'
    const toPx = (u: number) => u * GRID_SIZE
    // Right-align so the end of the text sits at the paper edge; bottom anchored by world gap
    const paperLabel = this.paperSpec.label
    const isANSI = paperLabel.startsWith('ANSI')
    const nominalLabel = isANSI ? 'Letter' : 'A4'
    const [family, orientation] = paperLabel.split('-')
    const originLines = [orientation.toLowerCase(), family]
    const xLabels = [
      { label: undefined, width: this.paperSpec.quarter.w },
      { label: nominalLabel, width: this.paperSpec.nominal.w },
    ]
    xLabels.forEach(({ label, width }) => {
      if (!label) return
      ctx.fillText(label, toPx(width), -gapWorld)
    })
    ctx.textAlign = 'right'
    xLabels.forEach(({ label, width }, idx) => {
      // Skip the first mirrored label to avoid the -A8 overlap near origin
      if (idx === 0 || !label) return
      ctx.fillText(label, -toPx(width), -gapWorld)
    })

    // Vertical axis labels (left of axis, 2px gap)
    // Need to measure width to place left of the axis
    // For vertical axis, place text to the left (1 grid unit, world) and bottom-align to the paper height
    ctx.textBaseline = 'bottom'
    const yLabels = [
      { label: undefined, height: this.paperSpec.quarter.h },
      { label: nominalLabel, height: this.paperSpec.nominal.h },
    ]
    const labelYOffset = GRID_SIZE
    yLabels.forEach(({ label, height }) => {
      if (!label) return
      const x = -gapWorld
      const y = toPx(height) + labelYOffset
      ctx.fillText(label, x, y)
    })
    ctx.textBaseline = 'bottom'
    yLabels.forEach(({ label, height }) => {
      if (!label) return
      const x = -gapWorld
      const y = -toPx(height) - labelYOffset
      ctx.fillText(label, x, y)
    })

    // Origin label using the horizontal label positioning rule
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    const lineHeight = LABEL_FONT_SIZE * 0.9
    const originX = -gapWorld
    const originY = -gapWorld
    originLines.forEach((line, index) => {
      ctx.fillText(line, originX, originY - (originLines.length - 1 - index) * lineHeight)
    })

    // Restore drawing state
    ctx.textBaseline = prevBaseline
    ctx.textAlign = prevAlign
    ctx.font = prevFont
    ctx.restore()
  }
  private getLayout(text: string, innerWidth: number, opts?: { literal?: boolean }): LayoutResult {
    const widthUnits = Math.round(innerWidth / GRID_SIZE)
    const cacheKey = `${hashText(text)}_${widthUnits}_${opts?.literal ? 'literal' : 'markdown'}`
    const existing = this.layoutCache.get(cacheKey)
    if (existing) return existing

    const tokens = opts?.literal ? [{ text }] : this.tokenizeMarkdown(text)
    const layout = this.layoutTokens(tokens, innerWidth)
    this.layoutCache.set(cacheKey, layout)
    return layout
  }

  private getParsedLayout(text: string, innerWidth: number): ParsedLayoutResult {
    const parsed = parseMarkdownCodeBlock(text)
    const segments = parsed.segments.length
      ? parsed.segments
      : [{ kind: parsed.isCodeBlock ? 'code' as const : 'markdown' as const, content: parsed.content }]

    const renderSegments: RenderSegmentLayout[] = []
    const linkRects: LinkRect[] = []
    let yOffset = 0
    let width = 0

    segments.forEach(segment => {
      const layout = this.getLayout(segment.content, innerWidth, { literal: segment.kind === 'code' })
      renderSegments.push({ kind: segment.kind, layout, yOffset })
      width = Math.max(width, layout.width)
      if (segment.kind === 'markdown') {
        layout.linkRects.forEach(rect => {
          linkRects.push({ ...rect, y: rect.y + yOffset })
        })
      }
      yOffset += layout.height
    })

    return {
      segments: renderSegments,
      width,
      height: yOffset,
      linkRects
    }
  }

  private tokenizeMarkdown(text: string): InlineToken[] {
    const tokens: InlineToken[] = []
    let i = 0
    const len = text.length
    while (i < len) {
      // Link: [label](href)
      if (text[i] === '[') {
        const closeLabel = text.indexOf(']', i + 1)
        const openHref = closeLabel >= 0 ? text.indexOf('(', closeLabel + 1) : -1
        const closeHref = openHref >= 0 ? text.indexOf(')', openHref + 1) : -1
        if (closeLabel > i + 1 && openHref === closeLabel + 1 && closeHref > openHref + 1) {
          const label = text.slice(i + 1, closeLabel)
          const href = text.slice(openHref + 1, closeHref).trim()
          const safeHref = this.safeHref(href)
          if (safeHref) {
            tokens.push({ text: label, href: safeHref })
            i = closeHref + 1
            continue
          }
        }
      }

      // Bold: **text**
      if (text[i] === '*' && text[i + 1] === '*') {
        const close = text.indexOf('**', i + 2)
        if (close > i + 2) {
          const content = text.slice(i + 2, close)
          tokens.push({ text: content, bold: true })
          i = close + 2
          continue
        }
      }

      // Italic: *text*
      if (text[i] === '*') {
        const close = text.indexOf('*', i + 1)
        if (close > i + 1) {
          const content = text.slice(i + 1, close)
          tokens.push({ text: content, italic: true })
          i = close + 1
          continue
        }
      }

      // Inline code: `code`
      if (text[i] === '`' && text.slice(i, i + 3) !== '```') {
        const close = text.indexOf('`', i + 1)
        if (close > i + 1) {
          const content = text.slice(i + 1, close)
          tokens.push({ text: content, code: true })
          i = close + 1
          continue
        }
      }

      // Plain text fallback
      const nextSpecial = (() => {
        const linkIdx = text.indexOf('[', i + 1)
        const boldIdx = text.indexOf('**', i + 1)
        const italIdx = text.indexOf('*', i + 1)
        const codeIdx = text.indexOf('`', i + 1)
        const candidates = [linkIdx, boldIdx, italIdx, codeIdx].filter(idx => idx >= 0)
        return candidates.length ? Math.min(...candidates) : -1
      })()
      const end = nextSpecial >= 0 ? nextSpecial : len
      tokens.push({ text: text.slice(i, end) })
      i = end
    }
    return tokens
  }

  private layoutTokens(tokens: InlineToken[], maxWidth: number): LayoutResult {
    const lines: LayoutLine[] = []
    let currentSpans: LayoutSpan[] = []
    let currentWidth = 0
    let separatorBefore = ''

    // Search needs to distinguish a visual wrap inside a word from a skipped
    // space or an authored newline. This does not change layout or cache keys.
    const pushLine = (nextSeparator = '') => {
      lines.push({ spans: currentSpans, width: currentWidth, separatorBefore })
      currentSpans = []
      currentWidth = 0
      separatorBefore = nextSeparator
    }

    const measure = (text: string, style?: InlineToken): number => {
      this.applyFontStyle(style)
      return this.ctx.measureText(text).width
    }

    const appendSpan = (text: string, width: number, style?: InlineToken) => {
      if (!text) return
      const last = currentSpans[currentSpans.length - 1]
      if (last && !!last.bold === !!style?.bold && !!last.italic === !!style?.italic && last.href === style?.href && !!last.code === !!style?.code) {
        last.text += text
        last.width += width
        currentWidth += width
        return
      }
      currentSpans.push({
        text,
        width,
        bold: style?.bold,
        italic: style?.italic,
        href: style?.href,
        code: style?.code,
      })
      currentWidth += width
    }

    const breakLongWord = (word: string, style?: InlineToken) => {
      let idx = 0
      while (idx < word.length) {
        if (currentWidth >= maxWidth && currentSpans.length) {
          pushLine()
        }
        const remaining = maxWidth - currentWidth
        if (remaining <= 0) {
          pushLine()
        }
        let take = 0
        let fitWidth = 0
        for (let j = 1; idx + j <= word.length; j++) {
          const slice = word.slice(idx, idx + j)
          const w = measure(slice, style)
          if (w <= maxWidth - currentWidth + 0.0001) {
            take = j
            fitWidth = w
          } else {
            break
          }
        }
        if (take === 0) {
          // Fallback: force one char
          take = 1
          fitWidth = measure(word.slice(idx, idx + 1), style)
        }
        const piece = word.slice(idx, idx + take)
        appendSpan(piece, fitWidth, style)
        idx += take
        if (currentWidth >= maxWidth - 0.0001) {
          pushLine()
        }
      }
    }

    const flushLineIfNeeded = (fragmentWidth: number, isWhitespace: boolean): boolean => {
      if (currentWidth === 0) {
        // Drop leading whitespace
        return false
      }
      if (currentWidth + fragmentWidth <= maxWidth + 0.0001) return false
      if (isWhitespace) {
        pushLine(' ')
        return true
      }
      pushLine()
      return true
    }

    tokens.forEach(token => {
      const segments = token.text.split(/(\n)/)
      segments.forEach(seg => {
        if (seg === '\n') {
          pushLine(' ')
          return
        }
        const parts = seg.match(/\S+|\s+/g) || []
        parts.forEach(part => {
          const isWhitespace = /^\s+$/.test(part)
          const partWidth = measure(part, token)
          if (partWidth > maxWidth + 0.0001 && !isWhitespace) {
            if (currentWidth > 0) {
              pushLine()
            }
            breakLongWord(part, token)
            return
          }
          if (flushLineIfNeeded(partWidth, isWhitespace)) {
            // If we broke before whitespace, skip adding it to avoid leading spaces
            if (isWhitespace) return
          }
          appendSpan(part, partWidth, token)
        })
      })
    })

    if (currentSpans.length || !lines.length) {
      lines.push({ spans: currentSpans, width: currentWidth, separatorBefore })
    }

    const lineCount = Math.max(1, lines.length)
    const contentWidth = lines.reduce((max, line) => Math.max(max, line.width), 0)
    const rawHeight = lineCount * LINE_HEIGHT + TEXT_PADDING_TOP_PX + TEXT_PADDING_BOTTOM_PX
    const height = Math.max(LINE_HEIGHT, rawHeight - TEXT_BOTTOM_TRIM_PX)

    const linkRects: LinkRect[] = []
    lines.forEach((line, idx) => {
      let x = 0
      const y = idx * LINE_HEIGHT
      line.spans.forEach(span => {
        if (span.href && span.width > 0) {
          linkRects.push({ href: span.href, x, y, width: span.width, height: LINE_HEIGHT })
        }
        x += span.width
      })
    })

    return { lines, width: contentWidth, height, linkRects }
  }

  private applyFontStyle(style?: { bold?: boolean, italic?: boolean }): void {
    const weight = style?.bold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_NORMAL
    const italic = style?.italic ? 'italic' : 'normal'
    this.ctx.font = `${italic} ${weight} ${FONT_SIZE}px ${FONT}`
  }

  private safeHref(href: string): string | undefined {
    if (!href) return undefined
    const trimmed = href.trim()
    const lower = trimmed.toLowerCase()
    if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('mailto:') || lower.startsWith('tel:')) {
      return trimmed
    }
    return undefined
  }

  hitTestLink(block: Block, worldX: number, worldY: number): string | undefined {
    const padX = TEXT_PADDING_X_UNITS * GRID_SIZE
    const innerWidth = Math.max(0, toPixels(block.xyzw[3]) - 2 * padX)
    const layout = this.getParsedLayout(block.text, innerWidth)
    const localX = worldX - toPixels(block.xyzw[0]) - padX
    const localY = worldY - toPixels(block.xyzw[1]) - TEXT_PADDING_TOP_PX
    if (localX < 0 || localY < 0) return undefined
    for (const rect of layout.linkRects) {
      if (localY >= rect.y && localY <= rect.y + rect.height && localX >= rect.x && localX <= rect.x + rect.width) {
        return rect.href
      }
    }
    return undefined
  }
}

// Lightweight FNV-1a 32-bit hash for strings; returns short hex
function hashText(str: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    // 32-bit FNV prime 16777619
    h = (h >>> 0) * 0x01000193 >>> 0
  }
  // 8-char hex is fine; keep it compact
  return (h >>> 0).toString(16)
}

function clamp(value: number, min: number, max: number): number {
  if (min > max) return value
  if (value < min) return min
  if (value > max) return max
  return value
}
